<template>
  <auth-page-wrapper container-class="notifications">
    <section>
      <h1>{{ getTranslations.notification_page_title }}</h1>

      <div class="flex-row notif_top">
        <p class="notif_numb">{{ totalNotifications }}
          {{ getTranslations.notification_page_total_notifications }}
        </p>

        <div class="flex-row mark_read" @click="handleMarkAllNotificationsAsRead">
          <template v-if="hasUnreadNotifications">
            <img src="/static/images/Checkmark.svg" alt="checkmark notifications"/>

            <p>{{ getTranslations.notification_page_mark_as_read }}</p>
          </template>
        </div>
      </div>
    </section>

    <section class="boxes_sect">
      <notification-list-item
        v-for="notification in formattedNotifications"
        :key="notification.id"
        :notification="notification"
        @notification:read="handleReadNotification"
      ></notification-list-item>

      <base-button
        v-if="hasNotifications"
        container-class="clear_noti"
        @button:click="handleClearNotifications"
      >
        {{ getTranslations.notification_page_clear_notifications_button }}
      </base-button>
    </section>

    <component
      v-if="notificationModalInfo.open"
      v-bind="notificationModalInfo.props"
      :is="notificationModalInfo.component"
      @redirect:levelsPage="redirectToLevelsPageFromNotificationModal"
      @game:start="handleGameStartFromNotificationModal"
      @redirect:storePage="redirectToStorePageFromNotificationModal"
      @play:lantern="handlePlayLanternGameFromNotificationModal"
      @play:puzzle="handlePlayPuzzleGameFromNotificationModal"
    ></component>

    <base-loader-modal v-if="isLoaderModalOpen" />

    <pre-game-modal v-if="isPreGameModalOpen" @close:modal="isPreGameModalOpen = false"></pre-game-modal>
  </auth-page-wrapper>
</template>

<script>
import {mapGetters, mapMutations, mapState, mapActions} from 'vuex'
import Vue from 'vue'
import BaseLoaderModal from '../common/modal/BaseLoaderModal';
import BaseButton from '../common/button/BaseButton'
import AuthPageWrapper from '../common/wrapper/AuthPageWrapper'
import PreLanternGameModal from '../common/modal/game/PreLanternGameModal';
import PrePuzzleGameModal from '../common/modal/game/PrePuzzleGameModal';
import NotificationListItem from './NotificationListItem'
import LevelUp from './modals/LevelUp'
import BundleDiscount from './modals/bonus_phases/BundleDiscount'
import BonusMultiplier from './modals/bonus_phases/PointsMultiplier'
import AchievementModal from './modals/AchievementModal'
import PreGameModal from './modals/pregame/PreGameModal'
import modals from '../../common/modals'
import {isNumber, isObject} from '../../helpers/common'
import {notificationConfigurationPerType, notificationTypeEnum} from '../../constants/notification'
import {achievementLevelNumberPerLevelName, achievementLevels} from '../../constants/achievement'

const moment = require('moment')

Vue.use(require('vue-moment'), {
  moment
})

export default {
  name: 'notifications',
  components: {
    BaseLoaderModal,
    BaseButton,
    AuthPageWrapper,
    PrePuzzleGameModal,
    PreLanternGameModal,
    NotificationListItem,
    LevelUp,
    BundleDiscount,
    BonusMultiplier,
    AchievementModal,
    PreGameModal
  },
  data () {
    return {
      notificationModalInfo: {open: false, component: null, props: null},
      isLoaderModalOpen: false,
      isPreGameModalOpen: false,
    }
  },
  beforeRouteEnter (to, from, next) {
    if (process.env.NODE_ENV !== 'production') {
      moment.locale(JSON.parse(JSON.stringify(localStorage.getItem('locale'))))
    } else {
      moment.locale(localStorage.getItem('locale'))
    }

    next()
  },
  created () {
    this.getUserNotifications()
  },
  computed: {
    ...mapGetters('i18n', [
      'getTranslations'
    ]),
    ...mapState('profile', [
      'notifications',
      'achievements'
    ]),
    ...mapState('common', [
      'userDetails',
    ]),
    achievementPerNotificationText () {
      return (Array.isArray(this.achievements) && isObject(this.achievements[0]) && Array.isArray(this.achievements[0].achievementDTOs))
        ? this.achievements[0].achievementDTOs.reduce((acc, {achievementName, achievementLevelDTOs}) => {
          if (Array.isArray(achievementLevelDTOs)) {
            achievementLevelDTOs.forEach(achievementLevelDTO => {
              if (isObject(achievementLevelDTO)) {
                acc[`${achievementName}_${achievementLevelDTO.achievementLevelName}`] = {
                  achievementDTO: achievementLevelDTO,
                  achievementName
                }
              }
            })
          }

          return acc
        }, {})
        : {}
    },
    formattedNotifications () {
      return this.notifications.map(notification => {
        const notificationAchievementName = this.getNotificationAchievementName(notification)
        const notificationType = this.getNotificationType(notification, notificationAchievementName)
        const notificationUserLevel = this.getNotificationUserLevel(notification, notificationType)
        const notificationBonusPoints = this.getNotificationBonusPoints(notification, notificationType)
        const notificationThreshold = this.getNotificationThreshold(notification, notificationType)
        const notificationMultiplier = this.getNotificationMultiplier(notification, notificationType)
        const notificationGameId = this.getNotificationGameId(notification, notificationType)
        const notificationTitle = this.getNotificationTitle(notification, notificationType, notificationUserLevel)
        const notificationDescription = this.getNotificationDescriptionAsList(notification, notificationType, notificationBonusPoints, notificationMultiplier)
        const {notificationIconSrc, notificationIconAlt} = this.getNotificationIcon(notification, notificationType, notificationAchievementName, notificationUserLevel, notificationMultiplier)
        const isNotificationUnread = (typeof notification.readStatus === 'boolean') && !notification.readStatus

        return {
          id: notification.id,
          startDate: notification.startDate || '',
          endDate: notification.endDate || '',
          date: notification.notificationDate || '',
          text: notification.text,
          // gamesEarned: isNumber(notification.gamesEarned) ? notification.gamesEarned : null,
          // expirationDate: notification.expirationDate || '',
          achievementName: notificationAchievementName,
          type: notificationType,
          userLevel: notificationUserLevel,
          bonusPoints: notificationBonusPoints,
          threshold: notificationThreshold,
          multiplier: notificationMultiplier,
          gameId: notificationGameId,
          title: notificationTitle,
          description: notificationDescription,
          iconSrc: notificationIconSrc,
          iconAlt: notificationIconAlt,
          isUnread: isNotificationUnread
        }
      })
    },
    totalNotifications () {
      return this.formattedNotifications.length
    },
    hasNotifications () {
      return this.totalNotifications > 0
    },
    totalUnreadNotifications () {
      return this.formattedNotifications.filter(({isUnread}) => isUnread).length
    },
    hasUnreadNotifications () {
      return this.totalUnreadNotifications > 0
    }
  },
  methods: {
    ...mapActions('profile', [
      'changeNotificationReadStatus',
      'getUserNotifications',
      'clearNotifications'
    ]),
    ...mapActions('game', [
      'getAddOnGameStatus',
    ]),
    ...mapMutations('profile', [
      'setNotificationReadStatus',
      'setAllNotificationsReadStatus'
    ]),
    ...mapMutations('common', [
      'setAddOnGameConfig',
      'resetAddOnGameConfig',
    ]),
    closeLoaderModalAsPromise ({ immediate = false } = {}) {
      return new Promise(resolve => {
        modals.hideCommonModal({
          immediate,
          afterHideCallback: () => {
            this.isLoaderModalOpen = false;
            resolve();
          },
        });
      });
    },
    async handleReadAddOnGameNotification (notification) {
      let canPlay = false;
      const gameId = notification.gameId;

      try {
        this.isLoaderModalOpen = true;

        modals.setupCommonModal({ hideManually: true });

        const queryParams = {
          gameId,
          msisdn: this.userDetails.msisdn,
        };

        const addOnGameStatusResponse = await this.getAddOnGameStatus({ queryParams });

        canPlay = addOnGameStatusResponse.canPlay;

        await this.closeLoaderModalAsPromise({immediate: true})
      } catch (_e) {
        await this.closeLoaderModalAsPromise();

        return;
      }

      this.setAddOnGameConfig({ gameId });

      if(notificationTypeEnum.ADD_ON_GAME_LANTERNS === notification.type) {
        this.notificationModalInfo.component = PreLanternGameModal
      }else if(notificationTypeEnum.ADD_ON_GAME_PUZZLE === notification.type) {
        this.notificationModalInfo.component = PrePuzzleGameModal
      } else {
        return;
      }

      this.notificationModalInfo.props = { canPlay };

      this.notificationModalInfo.open = true

      modals.setupCommonModal({
        showImmediate: true,
        afterHideCallback: () => {
          this.resetAddOnGameConfig();

          this.resetNotificationModalInfo();
        },
      });
    },
    getNotificationAchievementName (notification) {
      return this.achievementPerNotificationText.hasOwnProperty(notification.text)
        ? this.achievementPerNotificationText[notification.text].achievementName
        : null
    },
    getNotificationType (notification, notificationAchievementName) {
      const notificationCategoryEnum = notification && notification.notificationCategoryDTO && notification.notificationCategoryDTO.notifName

      if (!notificationCategoryEnum) { return '' }

      if (notificationCategoryEnum === 'CHANGE_USERNAME_OR_PIN') {
        return notificationTypeEnum.UPDATE_PROFILE
      } else if (notificationCategoryEnum === 'ACHIEVEMENT') {
        if (!notification.text || !this.achievementPerNotificationText[notification.text]) {
          return ''
        } else {
          return achievementLevels.includes(notificationAchievementName)
            ? notificationTypeEnum.ACHIEVEMENT_LEVEL
            : notificationTypeEnum.ACHIEVEMENT
        }
      } else if (notificationCategoryEnum === 'achievement_next_TODO') {  /** FIX_REQUIRED: PHASE_2 */
        return notificationTypeEnum.ACHIEVEMENT_NEXT
      } else if (notificationCategoryEnum === 'BONUS_POINTS_PHASE') {
        return notificationTypeEnum.BONUS_GAME_POINTS
      } else if (notificationCategoryEnum === 'BUNDLE_DISCOUNT') {
        return notificationTypeEnum.BONUS_BUNDLE_DISCOUNT
      } else if (notificationCategoryEnum === 'INVITE_REGISTRATION') {
        return notificationTypeEnum.INVITATION_REGISTRATION
      } else if (notificationCategoryEnum === 'PENDING_LIGHT_THE_LANTERNS_GAME') {
        return notificationTypeEnum.ADD_ON_GAME_LANTERNS;
      } else if (notificationCategoryEnum === 'PENDING_PUZZLE_GAME') {
        return notificationTypeEnum.ADD_ON_GAME_PUZZLE;
      } else {
        return ''
      }
    },
    getNotificationUserLevel (notification, notificationType) {
      if (notificationTypeEnum.ACHIEVEMENT_LEVEL === notificationType) {
        return (this.achievementPerNotificationText.hasOwnProperty(notification.text) &&
          this.achievementPerNotificationText[notification.text].achievementName &&
          achievementLevelNumberPerLevelName.hasOwnProperty(this.achievementPerNotificationText[notification.text].achievementName)
        )
          ? achievementLevelNumberPerLevelName[this.achievementPerNotificationText[notification.text].achievementName]
          : null
      } else {
        return isNumber(notification.userLevel) ? notification.userLevel : null
      }
    },
    getNotificationBonusPoints (notification, notificationType) {
      if ([notificationTypeEnum.ACHIEVEMENT, notificationTypeEnum.ACHIEVEMENT_LEVEL].includes(notificationType)) {
        return (this.achievementPerNotificationText.hasOwnProperty(notification.text) &&
          this.achievementPerNotificationText[notification.text].achievementDTO &&
          isNumber(this.achievementPerNotificationText[notification.text].achievementDTO.prizeValue)
        )
          ? this.achievementPerNotificationText[notification.text].achievementDTO.prizeValue
          : null
      } else {
        return isNumber(notification.bonusPoints) ? notification.bonusPoints : null
      }
    },
    getNotificationThreshold (notification, notificationType) {
      if (notificationTypeEnum.ACHIEVEMENT === notificationType) {
        return (this.achievementPerNotificationText.hasOwnProperty(notification.text) &&
          this.achievementPerNotificationText[notification.text].achievementDTO &&
          isNumber(this.achievementPerNotificationText[notification.text].achievementDTO.threshold)
        )
          ? this.achievementPerNotificationText[notification.text].achievementDTO.threshold
          : null
      } else {
        return null
      }
    },
    getNotificationMultiplier (notification, notificationType) {
      return (
        [notificationTypeEnum.BONUS_GAME_POINTS, notificationTypeEnum.BONUS_BUNDLE_DISCOUNT].includes(notificationType) &&
        notification &&
        isNumber(+notification.text)
      ) ? +notification.text
        : null
    },
    getNotificationGameId (notification, notificationType) {
      return [notificationTypeEnum.ADD_ON_GAME_LANTERNS, notificationTypeEnum.ADD_ON_GAME_PUZZLE].includes(notificationType)
        ? `${ notification.gameId }`
        : '';
    },
    getNotificationTitle (notification, notificationType, notificationUserLevel) {
      if (!notification || !notificationType || !notificationConfigurationPerType[notificationType]) { return '' }

      switch (notificationType) {
        case notificationTypeEnum.ACHIEVEMENT_LEVEL:
          return notificationConfigurationPerType[notificationType].getTitle({level: notificationUserLevel})
        case notificationTypeEnum.UPDATE_PROFILE:
        case notificationTypeEnum.ACHIEVEMENT:
        case notificationTypeEnum.ACHIEVEMENT_NEXT:
        case notificationTypeEnum.BONUS_GAME_POINTS:
        case notificationTypeEnum.BONUS_BUNDLE_DISCOUNT:
        case notificationTypeEnum.INVITATION_REGISTRATION:
        case notificationTypeEnum.ADD_ON_GAME_LANTERNS:
        case notificationTypeEnum.ADD_ON_GAME_PUZZLE:
          return notificationConfigurationPerType[notificationType].getTitle()
        default:
          return ''
      }
    },
    getNotificationDescriptionAsList (notification, notificationType, notificationBonusPoints, notificationMultiplier) {
      if (!notification || !notificationType || !notificationConfigurationPerType[notificationType]) { return [] }

      switch (notificationType) {
        case notificationTypeEnum.UPDATE_PROFILE:
        case notificationTypeEnum.ADD_ON_GAME_LANTERNS:
        case notificationTypeEnum.ADD_ON_GAME_PUZZLE:
          return notificationConfigurationPerType[notificationType].getDescriptionAsList()
        case notificationTypeEnum.ACHIEVEMENT:
        case notificationTypeEnum.ACHIEVEMENT_LEVEL:
        case notificationTypeEnum.ACHIEVEMENT_NEXT:
        case notificationTypeEnum.INVITATION_REGISTRATION:
          return notificationConfigurationPerType[notificationType].getDescriptionAsList({rewardPoints: notificationBonusPoints})
        case notificationTypeEnum.BONUS_GAME_POINTS:
        case notificationTypeEnum.BONUS_BUNDLE_DISCOUNT:
          const startDate = moment(notification.startDate)
          const endDate = moment(notification.endDate)
          const duration = Math.ceil(moment.duration(endDate.diff(startDate)).asHours())

          return notificationConfigurationPerType[notificationType].getDescriptionAsList({
            duration: duration > 0 ? duration : 0,
            multiplier: notificationMultiplier
          })
        default:
          return []
      }
    },
    getNotificationIcon (notification, notificationType, notificationAchievementName, notificationUserLevel, notificationMultiplier) {
      if (!notification || !notificationType || !notificationConfigurationPerType[notificationType]) { return {notificationIconSrc: '', notificationIconAlt: ''} }

      let iconInfo = null

      switch (notificationType) {
        case notificationTypeEnum.UPDATE_PROFILE:
        case notificationTypeEnum.ADD_ON_GAME_LANTERNS:
        case notificationTypeEnum.ADD_ON_GAME_PUZZLE:
        case notificationTypeEnum.BONUS_BUNDLE_DISCOUNT:
        case notificationTypeEnum.INVITATION_REGISTRATION:
        case notificationTypeEnum.ACHIEVEMENT_NEXT:
          iconInfo = notificationConfigurationPerType[notificationType].getIcon()
          break
        case notificationTypeEnum.ACHIEVEMENT:
          if (notificationAchievementName) {
            iconInfo = notificationConfigurationPerType[notificationType].getIcon({achievementName: notificationAchievementName})
          }
          break
        case notificationTypeEnum.ACHIEVEMENT_LEVEL:
          if (Object.values(achievementLevelNumberPerLevelName).includes(notificationUserLevel)) {
            iconInfo = notificationConfigurationPerType[notificationType].getIcon({level: notificationUserLevel})
          }
          break
        case notificationTypeEnum.BONUS_GAME_POINTS:
          if (notificationMultiplier > 1 && notificationMultiplier < 5) {
            iconInfo = notificationConfigurationPerType[notificationType].getIcon({multiplier: notificationMultiplier})
          }
          break
        default:
          break
      }

      if (iconInfo) {
        return iconInfo
      } else {
        return notification && notification.notificationCategoryDTO && notification.notificationCategoryDTO.imgUrl
          ? {notificationIconSrc: notification.notificationCategoryDTO.imgUrl, notificationIconAlt: ''}
          : {notificationIconSrc: '', notificationIconAlt: ''}
      }
    },
    handleMarkAllNotificationsAsRead () {
      if (this.hasUnreadNotifications) {
        this.changeNotificationReadStatus()

        this.setAllNotificationsReadStatus({readStatus: true})
      }
    },
    resetNotificationModalInfo () {
      this.notificationModalInfo.open = false
      this.notificationModalInfo.component = null
      this.notificationModalInfo.props = null
    },
    handleReadNotification (notification) {
      if (notification.isUnread) {
        this.$axios.get(`api/changeNotificationStatus?notificationId=${notification.id}`)
        this.setNotificationReadStatus({notificationId: notification.id, readStatus: true})
      }

      if (notificationTypeEnum.UPDATE_PROFILE === notification.type) {
        this.$router.push('/home/profile/edit')

        return
      } else if ([notificationTypeEnum.ADD_ON_GAME_LANTERNS, notificationTypeEnum.ADD_ON_GAME_PUZZLE].includes(notification.type)) {
        this.handleReadAddOnGameNotification(notification);

        return;
      } else if (notificationTypeEnum.ACHIEVEMENT === notification.type) {
        this.notificationModalInfo.component = AchievementModal
        this.notificationModalInfo.props = {achievement: {achievementName: notification.achievementName, prizeValue: notification.bonusPoints, threshold: notification.threshold, completed: true, recurring: false}}
      } else if (notificationTypeEnum.ACHIEVEMENT_LEVEL === notification.type) {
        this.notificationModalInfo.component = LevelUp
        this.notificationModalInfo.props = {playSound: false, level: notification.userLevel, rewardPoints: notification.bonusPoints}
      } else if (notificationTypeEnum.BONUS_BUNDLE_DISCOUNT === notification.type) {
        this.notificationModalInfo.component = BundleDiscount
        this.notificationModalInfo.props = {startDate: notification.startDate, endDate: notification.endDate, multiplier: notification.multiplier}
      } else if (notificationTypeEnum.BONUS_GAME_POINTS === notification.type) {
        this.notificationModalInfo.component = BonusMultiplier
        this.notificationModalInfo.props = {startDate: notification.startDate, endDate: notification.endDate, multiplier: notification.multiplier}
      } else {
        return
      }
      this.notificationModalInfo.open = true

      modals.setupCommonModal({afterHideCallback: this.resetNotificationModalInfo})
    },
    redirectToLevelsPageFromNotificationModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.resetNotificationModalInfo()

          this.$router.push('/home/levels')
        }
      })
    },
    redirectToStorePageFromNotificationModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.resetNotificationModalInfo()

          this.$router.push('/home/store')
        }
      })
    },
    handleGameStartFromNotificationModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.resetNotificationModalInfo()

          this.isPreGameModalOpen = true
        }
      })
    },
    handlePlayLanternGameFromNotificationModal() {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.resetNotificationModalInfo()

          this.$router.push({ name: 'addOnGameLantern' });
        }
      })
    },
    handlePlayPuzzleGameFromNotificationModal() {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.resetNotificationModalInfo()

          this.$router.push({ name: 'addOnGamePuzzle' });
        }
      })
    },
    handleClearNotifications () {
      this.clearNotifications()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/home/Notifications.vue